import React from 'react';
import { motion, type Variants } from 'motion/react';

/**
 * Shared, soft easing used across the whole landing page so every
 * appearance animation feels like it belongs to the same family.
 * (ease-out-expo style: starts a touch quick, settles very gently)
 */
export const SOFT_EASE = [0.22, 1, 0.36, 1] as const;
export const SOFT_DURATION = 0.8;

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

const getOffset = (direction: Direction, distance: number) => {
  switch (direction) {
    case 'up':
      return { y: distance, x: 0 };
    case 'down':
      return { y: -distance, x: 0 };
    case 'left':
      return { x: distance, y: 0 };
    case 'right':
      return { x: -distance, y: 0 };
    default:
      return { x: 0, y: 0 };
  }
};

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Direction the element travels in from. Defaults to a gentle rise. */
  direction?: Direction;
  /** Travel distance in px. Kept small for a calm, non-distracting motion. */
  distance?: number;
  delay?: number;
  duration?: number;
  /** Render as a semantic element. */
  as?: 'div' | 'section';
  /** Re-trigger every time it scrolls into view instead of just once. */
  once?: boolean;
}

/**
 * Reveals a block with a soft fade + slide as it scrolls into view.
 * Drop-in replacement for the previous `useInView` + tailwind transition pattern.
 */
export const Reveal: React.FC<RevealProps> = ({
  children,
  className,
  style,
  direction = 'up',
  distance = 28,
  delay = 0,
  duration = SOFT_DURATION,
  as = 'div',
  once = true,
}) => {
  const offset = getOffset(direction, distance);
  const MotionTag = as === 'section' ? motion.section : motion.div;

  return (
    <MotionTag
      className={className}
      style={style}
      initial={{ opacity: 0, ...offset }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once, amount: 0.2, margin: '0px 0px -8% 0px' }}
      transition={{ duration, ease: SOFT_EASE, delay }}
    >
      {children}
    </MotionTag>
  );
};

const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.05,
    },
  },
};

interface StaggerProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  once?: boolean;
  /** Gap in seconds between each child appearing. */
  stagger?: number;
}

/**
 * Container that gently cascades its `StaggerItem` children into view,
 * so grids (menu cards, core values, etc.) appear one-after-another.
 */
export const Stagger: React.FC<StaggerProps> = ({
  children,
  className,
  style,
  once = true,
  stagger,
}) => {
  const variants: Variants = stagger
    ? {
        hidden: {},
        visible: {
          transition: { staggerChildren: stagger, delayChildren: 0.05 },
        },
      }
    : containerVariants;

  return (
    <motion.div
      className={className}
      style={style}
      variants={variants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: 0.15, margin: '0px 0px -8% 0px' }}
    >
      {children}
    </motion.div>
  );
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: SOFT_DURATION, ease: SOFT_EASE },
  },
};

interface StaggerItemProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const StaggerItem: React.FC<StaggerItemProps> = ({
  children,
  className,
  style,
}) => (
  <motion.div className={className} style={style} variants={itemVariants}>
    {children}
  </motion.div>
);
